/**
 * StepEquipmentThreadChoice.jsx
 *
 * Conditional wizard sub-step that fires after EQUIPMENT_CLASS when the
 * picked class has multiple thread options (TH) or multiple shank options
 * (DTH). Writes to either `selections.threadSystem` (TH) or
 * `selections.shankType` (DTH) via the shared `handleEquipmentThreadChoice`
 * handler in WizardContext (which is productLine-aware).
 *
 * PORTED from TH ToolFinder v4:
 *   tophammer-toolfinder-v4/src/components/wizard/StepThreadChoice.jsx
 *   lines 96-158 (ThreadCard visual). The MRT premium-thread badge
 *   (sandvik-gold pill on CT55 / CT67 / LT90) is the headline TF v4
 *   feature we were missing in the original D3d render.
 *
 * TH path (productLine === 'TH'): renders the ported ThreadCard list.
 *   Each card shows a 16×16 colored thread badge (raw hex from
 *   THREAD_DETAILS.color), name + size + description + hole range, and a
 *   "Premium" pill badge for premium threads. Selected card gets an
 *   electric-blue checkmark on the right.
 *
 * DTH path (productLine === 'DTH'): keeps the generic TileSelector —
 *   shanks have no premium variant. shankChoices is a plain string array.
 *
 * Both paths feed the same handleEquipmentThreadChoice(value) callback.
 *
 * Source contract: getEquipmentClassById(id, productLine) returns either:
 *   - TH:  { threadChoices: [{thread, label, desc, productCount}, ...] }
 *   - DTH: { shankChoices: ['DHD3.5HD', 'M30', ...] }
 *
 * @module components/wizard/StepEquipmentThreadChoice
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D3d-update — premium badge port)
 */

import { useWizard } from '@contexts/WizardContext';
import TileSelector from '@components/ui/TileSelector';
import { getEquipmentClassById, THREAD_DETAILS } from '@data/wizardData';

/**
 * Single thread card row (TH path). Ported from TF v4 StepThreadChoice.jsx
 * lines 96-158. The 16×16 thread badge uses inline backgroundColor because
 * the color is data-driven (raw hex from THREAD_DETAILS).
 *
 * @param {Object} props
 * @param {string} props.thread     Thread system id (e.g. 'CT55', 'ST58').
 * @param {boolean} props.isSelected
 * @param {(thread: string) => void} props.onSelect
 */
function ThreadCard({ thread, isSelected, onSelect }) {
  const details = THREAD_DETAILS[thread] || {
    name: thread,
    size: `${thread} threads`,
    description: 'Standard thread system',
    holeRange: 'Various',
    color: '#1441F5',
  };

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={() => onSelect(thread)}
      className={[
        'w-full p-5 border-2 transition-all duration-200 text-left bg-white',
        'focus:outline-none focus:ring-2 focus:ring-electric-blue',
        isSelected
          ? 'border-electric-blue bg-eb-1/20 shadow-md'
          : 'border-n2 hover:border-electric-blue hover:shadow-lg',
      ].join(' ')}
    >
      <div className="flex items-center gap-4">
        {/* Thread Badge — inline backgroundColor is intentional (data-driven hex). */}
        <div
          className="w-16 h-16 flex items-center justify-center flex-shrink-0 font-sandvik-headline font-normal text-xl text-white"
          style={{ backgroundColor: details.color }}
          aria-hidden="true"
        >
          {details.name}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-sandvik-headline font-normal text-lg text-carbon-black">
              {details.size}
            </h3>
            {details.isPremium && (
              <span
                data-testid="premium-badge"
                className="px-2 py-0.5 bg-sandvik-gold text-carbon-black font-sandvik-text text-xs font-normal"
              >
                Premium
              </span>
            )}
          </div>
          <p className="font-sandvik-text text-sm text-n6 mb-1">
            {details.description}
          </p>
          <p className="font-sandvik-text text-xs text-n5">
            Hole range: {details.holeRange}
          </p>
        </div>

        {/* Checkmark — Sandvik brand allows a circle here (icon container, not a button corner). */}
        {isSelected && (
          <div
            className="flex-shrink-0 w-8 h-8 bg-electric-blue rounded-full flex items-center justify-center text-white"
            aria-hidden="true"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        )}
      </div>
    </button>
  );
}

export default function StepEquipmentThreadChoice() {
  const { selections, handleEquipmentThreadChoice } = useWizard();
  const isDth = selections.productLine === 'DTH';
  const eqClass = getEquipmentClassById(selections.equipmentClass, selections.productLine);

  // --- DTH path: plain TileSelector for shank picker ---
  if (isDth) {
    const tiles = (eqClass?.shankChoices || []).map((shank) => ({
      id: shank,
      label: shank,
      sublabel: '',
    }));

    return (
      <section aria-labelledby="thread-shank-heading" className="max-w-5xl mx-auto">
        <header className="mb-6">
          <h2
            id="thread-shank-heading"
            className="font-sandvik-headline text-2xl font-normal text-carbon-black"
          >
            Which shank type?
          </h2>
          <p className="mt-1 font-sandvik-text text-n6">
            Pick the shank you are running on your {eqClass?.label || 'hammer'}.
          </p>
        </header>

        {tiles.length === 0 ? (
          <p className="text-n6">No choices to pick &mdash; please go back and select an equipment class.</p>
        ) : (
          <TileSelector
            tiles={tiles}
            value={selections.shankType}
            onChange={handleEquipmentThreadChoice}
            cols={{ mobile: 2, tablet: 3, desktop: 4 }}
            variant="icon"
            ariaLabel="Shank type"
          />
        )}
      </section>
    );
  }

  // --- TH path: ported ThreadCard list with Premium badge ---
  const threadChoices = eqClass?.threadChoices || [];

  return (
    <section aria-labelledby="thread-shank-heading" className="max-w-3xl mx-auto">
      <header className="mb-6">
        <h2
          id="thread-shank-heading"
          className="font-sandvik-headline text-2xl font-normal text-carbon-black"
        >
          Which thread system?
        </h2>
        <p className="mt-1 font-sandvik-text text-n6">
          Pick the thread for your {eqClass?.label || 'class'}.
        </p>
      </header>

      {threadChoices.length === 0 ? (
        <p className="text-n6">No choices to pick &mdash; please go back and select an equipment class.</p>
      ) : (
        <div role="radiogroup" aria-label="Thread system" className="space-y-3">
          {threadChoices.map((c) => (
            <ThreadCard
              key={c.thread}
              thread={c.thread}
              isSelected={selections.threadSystem === c.thread}
              onSelect={handleEquipmentThreadChoice}
            />
          ))}
        </div>
      )}
    </section>
  );
}
